// Generic throttled-prompt scheduler core. Decides whether a keyed in-app prompt may
// show right now, based on a per-key cooldown persisted in localStorage so it survives
// reloads and navigation (mirrors the OTP-cooldown persistence pattern). Pure and
// framework-free — no React, no Apollo — so it unit-tests without a renderer or a fake
// clock. Calibration assignments are the first consumer; nothing here is calibration-aware.
// The React wrapper lives in lib/hooks/use-prompt-scheduler.ts.

// Per-key storage namespace; distinct from the OTP cooldown keys (`mea_otp_cooldown_`).
const STORAGE_PREFIX = 'mea_prompt_';

// Global default min interval between showings of a given prompt (config promptCooldownHours).
// Consumers may override per prompt-type.
export const DEFAULT_PROMPT_COOLDOWN_HOURS = 4;

const HOUR_MS = 60 * 60 * 1000;

const storageKey = (promptKey: string) => `${STORAGE_PREFIX}${promptKey}`;

// 'persistent' = localStorage (survives browser restarts); 'session' = sessionStorage
// (resets when the browser session/tab ends). Consumers pick per prompt-type.
export type PromptStorageScope = 'persistent' | 'session';

const getStore = (scope: PromptStorageScope): Storage =>
  (scope === 'session' ? window.sessionStorage : window.localStorage);

// Epoch ms this prompt was last shown/snoozed, or null if never shown (or storage unreadable).
export const readPromptLastShownAt = (
  promptKey: string,
  scope: PromptStorageScope = 'persistent',
): number | null => {
  try {
    const raw = getStore(scope).getItem(storageKey(promptKey));
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// Stamp the prompt as shown/snoozed at `at` (epoch ms) — starts a fresh cooldown window.
export const writePromptLastShownAt = (
  promptKey: string,
  at: number,
  scope: PromptStorageScope = 'persistent',
): void => {
  try {
    getStore(scope).setItem(storageKey(promptKey), String(at));
  } catch {
    // Best-effort: storage may be blocked (private mode / quota); cooldown stays in-memory.
    console.warn('prompt-scheduler: failed to persist prompt cooldown; continuing without persistence');
  }
};

// Forget a prompt's cooldown so it may show immediately. For sign-out resets and tests.
export const clearPromptCooldown = (
  promptKey: string,
  scope: PromptStorageScope = 'persistent',
): void => {
  try {
    getStore(scope).removeItem(storageKey(promptKey));
  } catch {
    console.warn('prompt-scheduler: failed to clear prompt cooldown (storage unavailable)');
  }
};

// Remove prompt cooldown keys (both stores) satisfying `matches`. The storage GETTER itself
// throws when access is blocked, so it is resolved inside the try.
const removePromptKeysWhere = (matches: (key: string) => boolean): void => {
  (['persistent', 'session'] as PromptStorageScope[]).forEach((scope) => {
    try {
      const store = getStore(scope);
      const keys: string[] = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (key && key.startsWith(STORAGE_PREFIX) && matches(key)) keys.push(key);
      }
      keys.forEach((key) => store.removeItem(key));
    } catch {
      console.warn('prompt-scheduler: failed to clear prompt cooldowns (storage unavailable)');
    }
  });
};

// Clear one user's prompt cooldowns (keys suffixed `:<userId>`) — called on sign-out so the
// reset stays scoped to the departing session's user, never the sibling tree's user.
export const clearPromptCooldownsForUser = (userId: string): void =>
  removePromptKeysWhere((key) => key.endsWith(`:${userId}`));

// True iff the cooldown has elapsed (or the prompt was never shown). `cooldownHours` and
// `now` are injectable so the gate stays a pure function the tests drive deterministically.
export const isPromptCooldownElapsed = (
  promptKey: string,
  cooldownHours: number = DEFAULT_PROMPT_COOLDOWN_HOURS,
  now: number = Date.now(),
  scope: PromptStorageScope = 'persistent',
): boolean => {
  const lastShownAt = readPromptLastShownAt(promptKey, scope);
  if (lastShownAt === null) return true;
  return now - lastShownAt >= cooldownHours * HOUR_MS;
};
